import type {
  ProjectDetail,
  WorkflowCitation,
  WorkflowException,
  WorkflowResult,
  ReviewIssue,
} from '@tinypro-audit/contracts';
import {
  comparisonCategory,
  REVIEW_AREA_LABELS,
  reviewNarrative,
  type ReviewCategory,
  comparisonReferenceKey,
  isActionableComparison,
} from '@tinypro-audit/contracts';

export type ReviewOrder = ProjectDetail['workflow']['orders'][number];
export type ReviewCheck =
  WorkflowResult['orders'][number]['stages'][number]['checks'][number];
export type StagedCheck = ReviewCheck & { stage: 'po' | 'acknowledgement' };
export type ReviewTopic = {
  key: string;
  order: ReviewOrder;
  field: string;
  title: string;
  kind: 'conflict' | 'clarification' | 'measurement';
  checks: StagedCheck[];
  allChecks: StagedCheck[];
  tasks: WorkflowException[];
  issue?: ReviewIssue;
};

export function topicCategory(topic: ReviewTopic): ReviewCategory {
  if (topic.kind === 'measurement') return 'stage1_follow_up';
  if (topic.kind === 'conflict') return 'document_conflict';
  const categories = topic.checks.map(comparisonCategory);
  return categories.includes('missing_evidence')
    ? 'missing_evidence'
    : 'clarification';
}

const fieldNames: Record<string, string> = {
  vinyl_trim_color: 'Trim color',
  vinyl_trim: 'Vinyl trim',
  window_placement: 'Window placement',
  track_width: 'Track width',
  track_radius: 'Track radius',
  spring_cycles: 'Spring cycles',
  assessment_coverage: REVIEW_AREA_LABELS.measurement_applicability,
  stage1_applicability: REVIEW_AREA_LABELS.measurement_applicability,
  stage1_evidence: 'Pre-site review evidence',
  measurement_assessment: REVIEW_AREA_LABELS.pre_site,
  measurement_form: 'Pre-site form',
  operator_bracket: 'Operator bracket',
  insulation_type: 'Insulation',
  insulation_rating: 'Insulation rating',
  framing_lumber: 'Framing',
  operator_supply: 'Opener supply',
};
export const readable = (value: string) =>
  value.replaceAll('_', ' ').replaceAll('-', ' ');
export const fieldLabel = (value: string) =>
  fieldNames[value] ??
  reviewNarrative(readable(value)).replace(/^./, (s) => s.toUpperCase());
export const isPurchasingTask = (task: WorkflowException) =>
  /^(po|acknowledgement)(:|$)/.test(task.stage);
export const taskRequiresDisposition = (task: WorkflowException) =>
  task.state !== 'resolved' &&
  (task.latest_outcome === 'match' ||
    (task.content.attention === 'information' &&
      (task.assigned_user_id !== null || task.acknowledged_at !== null)));

export const isOpenCheck = isActionableComparison;
export const topicHasField = (topic: ReviewTopic, field: string | null) =>
  topic.field === field || topic.checks.some((check) => check.field === field);

function taskMatchesCheck(
  task: WorkflowException,
  check: StagedCheck,
): boolean {
  return (
    task.stage === `${check.stage}:${check.comparison}` &&
    task.field === check.field &&
    task.opening_ref === check.opening_ref &&
    task.line_ref === check.line_ref
  );
}

const purchasingKind = (checks: ReviewCheck[]) =>
  checks.some(
    (c) =>
      c.outcome === 'discrepancy' &&
      (c.attention === 'action_required' || c.attention === undefined),
  )
    ? ('conflict' as const)
    : ('clarification' as const);

const checkRank = (check: ReviewCheck) =>
  (check.attention === 'action_required' ? 0 : 10) +
  (check.outcome === 'discrepancy' ? 0 : 4) +
  ({ critical: 0, high: 1, normal: 2, low: 3 }[check.severity ?? 'low'] ?? 3);

/** Group existing model judgments for display. Never merge or resolve task records. */
export function projectReviewModel(data: ProjectDetail) {
  const run = data.runs.find((r) => r.id === data.project.latest_run_id);
  const latestAttempt = data.runs.find(
    (r) => r.submission_id === data.project.latest_submission_id,
  );
  const orders = data.workflow.orders
    .filter(
      (o) =>
        run &&
        o.run_id === run.id &&
        o.submission_id === run.submission_id &&
        o.result,
    )
    .map((order) => ({ ...order, identity: order.result!.identity }));
  const topics: ReviewTopic[] = [];
  const preSiteReviews: ReviewTopic[] = [];
  const allChecks: StagedCheck[] = [];
  for (const order of orders) {
    const checks = order.result!.stages.flatMap((stage) =>
      stage.checks.map((check) => ({ ...check, stage: stage.stage })),
    );
    allChecks.push(...checks);
    const grouped = new Set<string>();
    const byRef = new Map(
      checks.map((check) => [comparisonReferenceKey(check), check]),
    );
    for (const issue of order.result!.review_issues ?? []) {
      const issueChecks = issue.check_refs.map((ref) =>
        byRef.get(comparisonReferenceKey(ref)),
      );
      const keys = issue.check_refs.map(comparisonReferenceKey);
      // Stored malformed/incomplete groups must never hide the original findings.
      if (
        !keys.length ||
        new Set(keys).size !== keys.length ||
        keys.some((key) => grouped.has(key)) ||
        issueChecks.some((check) => !check || !isOpenCheck(check))
      )
        continue;
      const related = issueChecks as StagedCheck[];
      keys.forEach((key) => grouped.add(key));
      topics.push({
        key: JSON.stringify([order.id, 'issue', issue.issue_ref]),
        order,
        issue,
        field: related[0].field,
        title: issue.title,
        kind: purchasingKind(related),
        checks: [...related].sort((a, b) => checkRank(a) - checkRank(b)),
        allChecks: related,
        tasks: data.workflow.exceptions.filter(
          (task) =>
            task.order_id === order.id &&
            related.some((check) => taskMatchesCheck(task, check)),
        ),
      });
    }
    for (const field of new Set(
      checks
        .filter(
          (check) =>
            isOpenCheck(check) && !grouped.has(comparisonReferenceKey(check)),
        )
        .map((c) => c.field),
    )) {
      const fieldChecks = checks.filter(
        (c) => c.field === field && !grouped.has(comparisonReferenceKey(c)),
      );
      const open = fieldChecks
        .filter(isOpenCheck)
        .sort((a, b) => checkRank(a) - checkRank(b));
      topics.push({
        key: JSON.stringify([order.id, field]),
        order,
        field,
        title: fieldLabel(field),
        // Use the model's attention classification. A cost clarification is not
        // promoted to a confirmed conflict solely by its raw comparison outcome.
        kind: purchasingKind(open),
        checks: open,
        allChecks: fieldChecks,
        tasks: data.workflow.exceptions.filter(
          (e) =>
            e.order_id === order.id &&
            isPurchasingTask(e) &&
            e.field === field &&
            (!order.result!.review_issues ||
              fieldChecks.some((check) => taskMatchesCheck(e, check))),
        ),
      });
    }
    preSiteReviews.push({
      key: JSON.stringify([order.id, 'measurement_applicability']),
      order,
      field: 'assessment_coverage',
      title: 'Order vs. pre-site review',
      kind: 'measurement',
      checks: [],
      allChecks: [],
      tasks: data.workflow.exceptions.filter(
        (e) =>
          e.order_id === order.id && e.stage === 'measurement_applicability',
      ),
    });
  }
  const priority = { conflict: 0, clarification: 1, measurement: 2 };
  topics.sort(
    (a, b) =>
      priority[a.kind] - priority[b.kind] ||
      a.order.order_ref.localeCompare(b.order.order_ref) ||
      a.title.localeCompare(b.title),
  );
  const current = Boolean(
    data.is_current &&
    run?.state === 'succeeded' &&
    run.result &&
    run.submission_id === data.project.latest_submission_id &&
    (!latestAttempt || latestAttempt.id === run.id),
  );
  const closedTopics = current
    ? topics.filter((topic) =>
        topic.checks.every((check) =>
          topic.tasks.some(
            (task) =>
              task.state === 'resolved' &&
              task.latest_run_id === run?.id &&
              taskMatchesCheck(task, check),
          ),
        ),
      )
    : [];
  const activeTopics = topics.filter((topic) => !closedTopics.includes(topic));
  const topicTaskIds = new Set(
    topics.flatMap((topic) => topic.tasks.map((task) => task.id)),
  );
  const followUps = data.workflow.exceptions.filter(
    (task) =>
      task.stage !== 'measurement_applicability' &&
      (taskRequiresDisposition(task) ||
        (!topicTaskIds.has(task.id) &&
          task.state !== 'resolved' &&
          task.content.attention !== 'information')),
  );
  const preSiteTaskIds = new Set(
    preSiteReviews.flatMap((review) => review.tasks.map((task) => task.id)),
  );
  return {
    run,
    latestAttempt,
    orders,
    topics: activeTopics,
    closedTopics,
    preSiteReviews,
    preSiteFollowUps: data.workflow.exceptions.filter(
      (task) =>
        task.stage === 'measurement_applicability' &&
        !preSiteTaskIds.has(task.id) &&
        task.state !== 'resolved',
    ),
    allChecks,
    current,
    followUps,
    conflicts: activeTopics.filter((t) => t.kind === 'conflict'),
    clarifications: activeTopics.filter((t) => t.kind === 'clarification'),
    measurements: preSiteReviews.filter(
      (review) =>
        !['covered', 'not_applicable'].includes(
          review.order.result!.measurement.state,
        ),
    ),
    matches: allChecks.filter((c) => c.outcome === 'match'),
    notes: allChecks.filter(
      (c) => c.outcome !== 'match' && c.attention === 'information',
    ),
  };
}

export function topicEvidence(topic: ReviewTopic): WorkflowCitation[] {
  return uniqueEvidence(
    topic.issue
      ? [...topic.issue.evidence, ...topic.allChecks.flatMap((c) => c.evidence)]
      : topic.kind === 'measurement'
        ? topic.order.result!.measurement.evidence
        : topic.allChecks.flatMap((c) => c.evidence),
  );
}
export function uniqueEvidence(items: WorkflowCitation[]): WorkflowCitation[] {
  return [
    ...new Map(
      items.map((e) => [JSON.stringify([e.kind, e.id, e.page, e.quote]), e]),
    ).values(),
  ];
}

export type EvidenceColumn = 'purchase' | 'po' | 'acknowledgement';
/** Read values from explicit comparison directions; never infer a missing value. */
export function comparisonValues(
  checks: StagedCheck[],
  column: EvidenceColumn,
) {
  const values = new Map<string, { value: string; scopes: string[] }>();
  for (const check of checks) {
    let value: string | null | undefined;
    if (
      column === 'purchase' &&
      ['po_vs_estimate', 'ack_vs_estimate'].includes(check.comparison)
    )
      value = check.expected_value;
    if (column === 'po' && check.comparison === 'po_vs_estimate')
      value = check.actual_value;
    if (column === 'po' && check.comparison === 'ack_vs_po')
      value = check.expected_value;
    if (
      column === 'acknowledgement' &&
      ['ack_vs_po', 'ack_vs_estimate'].includes(check.comparison)
    )
      value = check.actual_value;
    if (value === undefined) continue;
    const text = value ?? 'Not established';
    const key = text.trim().replace(/\s+/g, ' ').toLowerCase();
    const item = values.get(key) ?? { value: text, scopes: [] };
    const scope = check.opening_ref ?? check.line_ref;
    if (scope && !item.scopes.includes(scope)) item.scopes.push(scope);
    values.set(key, item);
  }
  return [...values.values()];
}

export function openingLabel(
  data: ProjectDetail,
  ref: string,
  submissionId?: string | null,
) {
  const submission = data.submissions.find(
    (s) => s.id === (submissionId ?? data.project.latest_submission_id),
  );
  return (
    submission?.manifest.workflow?.openings.find((o) => o.opening_ref === ref)
      ?.label ?? fieldLabel(ref)
  );
}

export function topicScope(topic: ReviewTopic, data: ProjectDetail) {
  const refs =
    topic.kind === 'measurement'
      ? topic.order.identity.opening_refs
      : [
          ...new Set(
            topic.checks
              .map((c) => c.opening_ref ?? c.line_ref)
              .filter((v): v is string => Boolean(v)),
          ),
        ];
  return refs.length
    ? refs
        .map((ref) => openingLabel(data, ref, topic.order.submission_id))
        .join(' · ')
    : 'Order-wide';
}

export function topicDescription(topic: ReviewTopic, data: ProjectDetail) {
  if (topic.issue) return topic.issue.explanation;
  if (topic.kind === 'measurement')
    return topic.order.result!.measurement.explanation;
  const finding = data.runs
    .find((r) => r.id === topic.order.run_id)
    ?.result?.findings.find(
      (f) =>
        f.field === topic.field &&
        topic.checks.some((c) => c.opening_ref === f.opening_ref),
    );
  // Findings lack order IDs: only use a narrative when this field belongs to a
  // single reviewed order, otherwise use the explicitly scoped comparison.
  const matchingOrders = data.workflow.orders.filter(
    (o) =>
      o.run_id === topic.order.run_id &&
      o.result?.stages.some((s) =>
        s.checks.some((c) => c.field === topic.field),
      ),
  );
  return matchingOrders.length === 1 && finding
    ? finding.message
    : (topic.checks[0]?.explanation ?? '');
}

export function documentRole(document: ProjectDetail['documents'][number]) {
  const workflow = document.metadata.workflow;
  return typeof workflow === 'object' && workflow && 'role' in workflow
    ? String(workflow.role)
    : '';
}

export function columnEvidence(
  topic: ReviewTopic,
  data: ProjectDetail,
  column: EvidenceColumn,
) {
  const roles: Record<EvidenceColumn, string[]> = {
    purchase: ['estimate', 'signed_estimate', 'change_order', 'approval'],
    po: ['submitted_po', 'draft_po'],
    acknowledgement: ['acknowledgement', 'manufacturer_confirmation'],
  };
  return topicEvidence(topic).filter((e) => {
    const document = data.documents.find((d) => d.id === e.id);
    return (
      e.kind === 'document' &&
      document &&
      roles[column].includes(documentRole(document))
    );
  });
}
